import { Link } from "react-router-dom";
import { Check, ArrowRight, MessageCircle, Phone, ArrowUpRight, Building2, Truck } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { useSEO } from "@/components/site/SEO";
import { SITE, whatsappLink, telLink } from "@/config/site";
import { track } from "@/lib/analytics";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingCTA } from "@/components/site/FloatingCTA";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { LandlordForm } from "@/sections/LandlordForm";

const HELP_WHEN = [
  "A tenant moves out",
  "A new tenant is moving in",
  "A property needs deep cleaning",
  "Rubbish or unwanted furniture is left behind",
  "Furniture needs to be removed or transported",
  "A property needs to be prepared for viewings",
  "Multiple properties require recurring services",
];

const SERVICES = [
  "End of tenancy cleaning", "Deep cleaning", "Move-in cleaning", "Apartment cleaning",
  "House cleaning", "Oven cleaning", "Carpet cleaning", "Rubbish removal",
  "Furniture removal", "Office and common-area cleaning",
];

const GOVANS_ITEMS = ["Furniture transport", "Tenant removals", "Storage moves", "Unwanted furniture removal", "Apartment and house moves"];

export default function LandlordsPage() {
  useLenis();
  useSEO({
    title: "Cleaning Services for Landlords & Estate Agents Dublin | GoCleaning",
    description: "Reliable property cleaning and move-out support for landlords, estate agents and property managers across Dublin and Kildare. End of tenancy, deep cleaning, rubbish and furniture removal.",
    canonical: `${SITE.url}/landlords-estate-agents`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service",
      serviceType: "Cleaning Services for Landlords and Estate Agents",
      name: "Cleaning Services for Landlords & Estate Agents",
      description: "Property cleaning and move-out support for landlords, estate agents and property managers across Dublin and Kildare.",
      areaServed: ["Dublin", "County Kildare"], url: `${SITE.url}/landlords-estate-agents`,
      provider: { "@type": "CleaningService", name: SITE.name, telephone: SITE.phoneHref, email: SITE.email, url: SITE.url },
    },
  });

  return (
    <div className="App bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-white pt-32 pb-12 sm:pt-36" data-testid="landlords-hero">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
                <Building2 size={14} /> For property professionals
              </span>
              <h1 className="mt-5 max-w-3xl font-display text-5xl font-black leading-[1.03] tracking-tighter text-slate-900 sm:text-6xl">
                Cleaning Services for Landlords &amp; Estate Agents
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-slate-600">
                Reliable property cleaning and move-out support for landlords, estate agents and
                property managers across Dublin and surrounding areas.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="#landlord-form" data-testid="landlords-cta" className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-7 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-800">
                  Request a Landlord / Agent Quote <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </a>
                <a href={telLink()} onClick={track.phoneClick} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-4 text-base font-bold text-slate-800 hover:border-emerald-600 hover:text-emerald-700"><Phone size={18} /> Call {SITE.phoneDisplay}</a>
                <a href={whatsappLink("Hello, I'm a landlord / estate agent and would like a quote for property cleaning.")} onClick={() => track.whatsappClick("landlords")} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 text-base font-bold text-white hover:scale-105 transition-transform"><MessageCircle size={18} /> WhatsApp</a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* When we help */}
        <section className="bg-slate-50 py-16 sm:py-24" data-testid="landlords-when">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal><h2 className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">We can help when…</h2></Reveal>
            <Stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {HELP_WHEN.map((h) => (
                <StaggerItem key={h}>
                  <div className="flex h-full items-start gap-3 rounded-2xl bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-white"><Check size={15} /></span>
                    <span className="font-semibold text-slate-700">{h}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* Services */}
        <section className="bg-white py-16 sm:py-24" data-testid="landlords-services">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Services for your properties</h2>
              <p className="mt-3 max-w-2xl text-slate-600">One local team for cleaning and property turnover across your portfolio.</p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {SERVICES.map((s) => (
                <div key={s} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 px-4 py-3.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700"><Check size={15} /></span>
                  <span className="font-semibold text-slate-700">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* One contact for cleaning & removals (GoVans) */}
        <section className="bg-slate-50 py-16" data-testid="landlords-govans">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-8 sm:p-10 lg:p-12">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white"><Truck size={20} /></span>
                <h2 className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">One Contact for Cleaning &amp; Removals</h2>
              </div>
              <p className="mt-4 max-w-3xl text-slate-600">
                Through our related service{" "}
                <a href={SITE.goVans.url} target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-700 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-800">GoVans.ie</a>,
                landlords and agents can also arrange:
              </p>
              <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {GOVANS_ITEMS.map((g) => (
                  <li key={g} className="flex items-center gap-2 rounded-2xl bg-slate-50 px-4 py-3 font-semibold text-slate-700"><Check size={16} className="text-emerald-600" /> {g}</li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a href={SITE.goVans.url} target="_blank" rel="noopener noreferrer" data-testid="landlords-visit-govans" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3.5 font-bold text-white transition-colors hover:bg-slate-800">
                  Visit GoVans.ie <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <Link to="/moving-cleaning-packages" className="font-bold text-emerald-700 hover:underline">See combined moving + cleaning packages →</Link>
              </div>
              <p className="mt-4 text-xs text-slate-400">GoCleaning and GoVans are separate businesses run by the same local team.</p>
            </div>
          </div>
        </section>

        {/* Form + recurring note */}
        <section className="bg-emerald-50 py-16 sm:py-24" data-testid="landlords-form-section">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 rounded-3xl border border-emerald-200 bg-white p-6 text-center sm:p-8">
              <h2 className="font-display text-2xl font-bold text-slate-900">Interested in regular work?</h2>
              <p className="mt-2 text-slate-600">We welcome enquiries from landlords, estate agents and property managers who require recurring cleaning, removals or property turnover services.</p>
            </div>
            <div className="mb-8 text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-emerald-700">B2B enquiry</p>
              <h2 className="mt-3 font-display text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">Request a Landlord / Agent Quote</h2>
            </div>
            <LandlordForm />
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
